/**
 * Budget JSON Utilities
 *
 * These utilities handle automatic serialization and deserialization of form values
 * to/from the budget_json column using the field configurations defined in
 * budgetJsonFieldConfigs.js
 */

import { getAllFieldsForPlatform } from './budgetJsonFieldConfigs';

/**
 * Set a value at a nested path in an object
 * @param {object} obj - The object to modify
 * @param {string} path - Dot-separated path (e.g., 'geo.countries')
 * @param {any} value - The value to set
 */
function setNestedPath(obj, path, value) {
  const keys = path.split('.');
  let current = obj;

  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
}

/**
 * Get a value from a nested path in an object
 * @param {object} obj - The object to read from
 * @param {string} path - Dot-separated path (e.g., 'geo.countries')
 * @returns {any} The value at the path, or undefined if not found
 */
function getNestedPath(obj, path) {
  const keys = path.split('.');
  let current = obj;

  for (let i = 0; i < keys.length; i += 1) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[keys[i]];
  }

  return current;
}

/**
 * Format a form value for storage in budget_json based on format type
 * @param {any} value - The form value
 * @param {string} format - The format type from field config
 * @returns {any} The formatted value for budget_json
 */
function formatValueForStorage(value, format) {
  if (value === null || value === undefined || value === '') {
    return undefined; // Don't store empty values
  }

  switch (format) {
    case 'csv_array':
      // "a, b, c" -> ["a", "b", "c"]
      // Return empty array for empty values to maintain structure
      if (value === null || value === undefined || value === '') {
        return [];
      }
      if (typeof value === 'string') {
        return value
          .split(',')
          .map(item => item.trim())
          .filter(item => item);
      }
      return value;

    case 'select_value':
      // {label: "Label", value: "value"} -> "value"
      if (value && typeof value === 'object' && 'value' in value) {
        return value.value;
      }
      return value;

    case 'select_to_array':
      // {label: "Label", value: "value"} -> ["value"]
      if (value && typeof value === 'object' && 'value' in value) {
        return [value.value];
      }
      return value;

    case 'multiselect_values':
      // [{label: "L1", value: "v1"}, ...] -> ["v1", ...]
      if (Array.isArray(value)) {
        return value.map(item => ((item && typeof item === 'object' && 'value' in item) ? item.value : item));
      }
      return value;

    case 'number':
      // "123" -> 123
      if (typeof value === 'string') {
        const num = parseInt(value, 10);
        return Number.isNaN(num) ? undefined : num;
      }
      return typeof value === 'number' ? value : undefined;

    default:
      // No transformation
      return value;
  }
}

/**
 * Parse a budget_json value for display in form based on format type
 * @param {any} value - The value from budget_json
 * @param {string} format - The format type from field config
 * @param {string} type - The field type
 * @returns {any} The parsed value for form display
 */
function parseValueForDisplay(value, format) {
  if (value === null || value === undefined) {
    return undefined;
  }

  switch (format) {
    case 'csv_array':
      // ["a", "b", "c"] -> "a, b, c"
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return value;

    case 'select_value':
      // "value" -> {label: "Label", value: "value"}
      // Note: We can't reconstruct the label here, so we just return the value
      // The form will need to match it against options
      if (typeof value === 'string' || typeof value === 'number') {
        return value; // Form components will handle mapping to {label, value}
      }
      return value;

    case 'select_to_array':
      // [value] -> value (extract first element for single select)
      if (Array.isArray(value) && value.length > 0) {
        return value[0]; // Return first value for select field
      }
      return value;

    case 'multiselect_values':
      // ["v1", "v2"] -> values (form component will map to label/value objects)
      if (Array.isArray(value)) {
        return value; // Return array as-is, form component handles mapping
      }
      return value;

    case 'number':
      // 123 -> 123 (already a number)
      return value;

    default:
      // No transformation
      return value;
  }
}

/**
 * Serialize form data to budget_json structure using field configs
 *
 * @param {object} formData - The form data object
 * @param {string} platform - Platform key (e.g., 'google', 'meta', 'amazon')
 * @param {string} budgetType - Budget type (e.g., 'google_search', 'dsp')
 * @param {object} existingBudgetJson - Existing budget_json to merge with (optional)
 * @returns {object} The budget_json object ready for storage
 */
export function serializeToBudgetJson(formData, platform, budgetType, existingBudgetJson = {}) {
  // Start with existing budget_json to preserve special fields (like allocation)
  const budgetJson = { ...existingBudgetJson };

  // Get field configs for this platform
  const fields = getAllFieldsForPlatform(platform);

  // Filter fields by budget type if specified
  const relevantFields = budgetType
    ? fields.filter(field => !field.showForBudgetTypes || field.showForBudgetTypes.includes(budgetType))
    : fields;

  // Process each field
  relevantFields.forEach(field => {
    const formValue = formData[field.name];

    // Format the value for storage
    const formattedValue = formatValueForStorage(formValue, field.format);

    // Always set csv_array fields (even if empty) to maintain structure
    // For other formats, only set if we have a value
    if (formattedValue !== undefined || field.format === 'csv_array') {
      setNestedPath(budgetJson, field.budgetJsonPath, formattedValue !== undefined ? formattedValue : []);
    }
  });

  return budgetJson;
}

/**
 * Deserialize budget_json to form initial values using field configs
 *
 * @param {object} budgetJson - The budget_json object from database
 * @param {string} platform - Platform key (e.g., 'google', 'meta', 'amazon')
 * @param {string} budgetType - Budget type (e.g., 'google_search', 'dsp')
 * @returns {object} Form initial values object
 */
export function deserializeFromBudgetJson(budgetJson, platform, budgetType) {
  const formValues = {};

  if (!budgetJson || typeof budgetJson !== 'object') {
    return formValues;
  }

  // Get field configs for this platform
  const fields = getAllFieldsForPlatform(platform);

  // Filter fields by budget type if specified
  const relevantFields = budgetType
    ? fields.filter(field => !field.showForBudgetTypes || field.showForBudgetTypes.includes(budgetType))
    : fields;

  // Process each field
  relevantFields.forEach(field => {
    const jsonValue = getNestedPath(budgetJson, field.budgetJsonPath);

    if (jsonValue !== undefined) {
      // Parse the value for display
      formValues[field.name] = parseValueForDisplay(jsonValue, field.format);
    } else if (field.defaultValue !== undefined) {
      // Use default value if no value in budget_json
      formValues[field.name] = field.defaultValue;
    }
  });

  return formValues;
}

/**
 * Get default form values for a platform based on field configs
 * Used when creating new budgets
 *
 * @param {string} platform - Platform key (e.g., 'google', 'meta', 'amazon')
 * @param {string} budgetType - Budget type (e.g., 'google_search', 'dsp')
 * @returns {object} Form initial values with defaults
 */
export function getDefaultFormValues(platform, budgetType) {
  const formValues = {};

  // Get field configs for this platform
  const fields = getAllFieldsForPlatform(platform);

  // Filter fields by budget type if specified
  const relevantFields = budgetType
    ? fields.filter(field => !field.showForBudgetTypes || field.showForBudgetTypes.includes(budgetType))
    : fields;

  // Set default values
  relevantFields.forEach(field => {
    if (field.defaultValue !== undefined) {
      // For select fields with default values, we need to return them in a format
      // that matches what the form expects
      if (field.format === 'select_value' && typeof field.defaultValue === 'string') {
        // We just return the value, the form initialization will handle mapping to options
        formValues[field.name] = field.defaultValue;
      } else if (field.format === 'csv_array' && Array.isArray(field.defaultValue)) {
        // Convert array to CSV string for display
        formValues[field.name] = field.defaultValue.join(', ');
      } else if (field.format === 'multiselect_values' && Array.isArray(field.defaultValue)) {
        // Keep as array for multiselect
        formValues[field.name] = field.defaultValue;
      } else {
        formValues[field.name] = field.defaultValue;
      }
    }
  });

  return formValues;
}

/**
 * Validate form values against field configs
 * Returns an object with field names as keys and error messages as values
 *
 * @param {object} formData - The form data to validate
 * @param {string} platform - Platform key
 * @param {string} budgetType - Budget type
 * @returns {object} Validation errors object {fieldName: errorMessage}
 */
export function validateBudgetJsonFields(formData, platform, budgetType) {
  const errors = {};

  // Get field configs for this platform
  const fields = getAllFieldsForPlatform(platform);

  // Filter fields by budget type if specified
  const relevantFields = budgetType
    ? fields.filter(field => !field.showForBudgetTypes || field.showForBudgetTypes.includes(budgetType))
    : fields;

  // Validate each required field
  relevantFields.forEach(field => {
    if (field.required) {
      const value = formData[field.name];
      if (value === null || value === undefined || value === '') {
        errors[field.name] = `${field.label} is required`;
      }
    }
  });

  return errors;
}
